// Meta Pixel helper.
//
// The base snippet and the pixel id live in public/index.html; this is only the
// thin call site the app uses. It exists so that an ad blocker, a consent tool
// or a stripped-down browser — any of which can leave `fbq` undefined — can
// never take a form submit down with it.

const fbq = () => (typeof window !== 'undefined' && typeof window.fbq === 'function' ? window.fbq : null);

/** A standard Meta event ('Lead', 'ViewContent', 'Contact', …). */
export const track = (event, params, options) => {
  const f = fbq();
  if (!f) return;
  try {
    if (options) f('track', event, params, options);
    else f('track', event, params);
  } catch {
    /* the pixel is analytics, never a reason to break the page */
  }
};

const readCookie = (name) => {
  if (typeof document === 'undefined') return '';
  const prefix = `${name}=`;
  const item = document.cookie
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(prefix));
  return item ? decodeURIComponent(item.slice(prefix.length)) : '';
};

const eventId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `lead-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
};

const clickCookie = () => {
  const cookie = readCookie('_fbc');
  if (cookie) return cookie;
  if (typeof window === 'undefined') return '';
  const fbclid = new URLSearchParams(window.location.search).get('fbclid');
  return fbclid ? `fb.1.${Date.now()}.${fbclid}` : '';
};

/**
 * A short, human-readable reference for one enquiry.
 *
 * Deliberately readable rather than a UUID: its job is to be copied off an
 * email into a spreadsheet by hand, so it has to survive being retyped.
 */
export const leadReference = () => {
  const now = new Date();
  const stamp = [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, '0'),
    String(now.getDate()).padStart(2, '0'),
  ].join('');
  const suffix = Math.random().toString(36).slice(2, 6).toUpperCase();
  return `GG-${stamp}-${suffix}`;
};

/**
 * Reports one confirmed website lead through both Pixel and the server-side
 * Conversions API. The shared event ID lets Meta deduplicate the two copies.
 * CAPI failure is deliberately non-blocking: a tracking service must never
 * turn a successfully delivered enquiry into an error for the parent.
 */
export const trackLead = ({
  phone,
  firstName,
  reference,
  contentName,
  contentCategory,
  leadSource,
}) => {
  const id = eventId();
  track('Lead', {
    content_name: contentName,
    content_category: contentCategory,
  }, { eventID: id });

  if (typeof window !== 'undefined' && typeof fetch === 'function') {
    fetch('/api/meta-conversion.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
      body: JSON.stringify({
        eventId: id,
        phone,
        // Hashed server-side alongside the phone. The name is already being
        // collected, and every extra identifier raises the share of leads Meta
        // can match back to a real person.
        firstName,
        // Our own id for this lead, not Meta's. It goes out with the enquiry
        // email too, so a lead in the inbox can be tied to the ad click that
        // produced it — and later, to whether it turned into a student.
        reference,
        contentName,
        contentCategory,
        leadSource,
        sourceUrl: window.location.href.split('#')[0],
        fbp: readCookie('_fbp'),
        fbc: clickCookie(),
      }),
    }).catch(() => {
      /* Pixel tracking above remains available if the API is unreachable. */
    });
  }

  return id;
};

/** A custom event — anything not on Meta's standard list. */
export const trackCustom = (event, params) => {
  const f = fbq();
  if (!f) return;
  try {
    f('trackCustom', event, params);
  } catch {
    /* see above */
  }
};
